import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  useTheme,
  alpha,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  FormGroup,
  FormControlLabel,
  Checkbox,
  FormLabel
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Campaign as CampaignIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { dashboardApi } from '../../services/api';
import SnapshotDataService from '../../services/SnapshotDataService';

/**
 * MonthlyByMonthTable - Bảng hiển thị dữ liệu theo tháng với brand expandable
 */
const MonthlyByMonthTable = ({ 
  loading: parentLoading = false,
  selectedSnapshotMonth = null, // For snapshot mode
  snapshotData = null, // Pre-loaded snapshot data
  enhancedMetrics = null // Backend financial metrics for consistency
}) => {
  const theme = useTheme();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedBrands, setExpandedBrands] = useState(new Set());
  const [selectedMetric, setSelectedMetric] = useState('actual_cost');
  const [mediaFilters, setMediaFilters] = useState(['all']); // Changed to array for multi-select
  const [isSnapshotMode, setIsSnapshotMode] = useState(false);
  const [brandFilter, setBrandFilter] = useState('all');
  const [availableBrands, setAvailableBrands] = useState([]);

  // Media filter options
  const mediaFilterOptions = [
    { value: 'all', label: 'All Media', color: 'primary' },
    { value: 'social', label: 'Social Media', color: 'success' },
    { value: 'paid', label: 'Paid Media', color: 'warning' }
  ];

  // Fetch data - supports both current and snapshot modes
  const fetchMonthlyData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      let response;
      
      if (selectedSnapshotMonth) {
        // Snapshot mode - get budget data from snapshot
        console.log('📈 Fetching snapshot monthly data for:', selectedSnapshotMonth);
        
        // Use pre-loaded snapshot data if available
        if (snapshotData?.budgetData) {
          response = { data: snapshotData.budgetData };
        } else {
          // Try new snapshot structure first
          try {
            const newSnapshotResponse = await SnapshotDataService.getNewSnapshotBrandSummaries(selectedSnapshotMonth);
            if (newSnapshotResponse?.data?.brand_summaries) {
              console.log('✅ Using new snapshot structure for monthly data');
              response = { data: newSnapshotResponse.data.brand_summaries };
            } else {
              // Fallback to old snapshot structure
              const snapshotResponse = await SnapshotDataService.getSnapshotData(selectedSnapshotMonth);
              response = { data: snapshotResponse.brandSummaries || [] };
            }
          } catch (error) {
            console.warn('⚠️ New snapshot structure failed, falling back to old:', error);
            const snapshotResponse = await SnapshotDataService.getSnapshotData(selectedSnapshotMonth);
            response = { data: snapshotResponse.brandSummaries || [] };
          }
        }
      } else {
        // Current mode - use regular budget data API
        console.log('📈 Fetching current monthly data');
        response = await dashboardApi.getBudgetData();
      }
      
      const processedData = processBudgetDataForMonthlyView(response.data);
      // Store original data for multiple filter processing
      processedData.originalData = response.data;
      processedData.isSnapshot = !!selectedSnapshotMonth;
      processedData.snapshotMonth = selectedSnapshotMonth;
      setData(processedData);
      
      console.log('✅ Monthly data processed:', {
        mode: selectedSnapshotMonth ? 'snapshot' : 'current',
        snapshotMonth: selectedSnapshotMonth,
        brandsCount: processedData.brands.length,
        monthsCount: processedData.months.length
      });
      
    } catch (err) {
      console.error('❌ Error fetching monthly data:', err);
      setError(`Failed to load ${selectedSnapshotMonth ? 'snapshot' : 'current'} monthly data`);
      // Tạo mock data để test UI
      const mockData = generateMockData();
      mockData.originalData = []; // Empty original data for mock
      setData(mockData);
    } finally {
      setLoading(false);
    }
  };

  // Update snapshot mode detection
  useEffect(() => {
    setIsSnapshotMode(!!selectedSnapshotMonth);
  }, [selectedSnapshotMonth]);
  
  useEffect(() => {
    fetchMonthlyData();
    setExpandedBrands(new Set()); // Reset expanded brands when filters change
  }, [mediaFilters, brandFilter, selectedSnapshotMonth, snapshotData]); // Re-fetch when filters or snapshot data changes

  /**
   * Kiểm tra xem media type có thuộc filter hay không
   */
  const isMediaTypeIncluded = (mediaType, filterType = null) => {
    if (!mediaType) return true;
    
    const mediaTypeLower = mediaType.toLowerCase();
    
    // Các từ khóa để xác định Social media
    const socialKeywords = ['social', 'facebook', 'instagram', 'twitter', 'linkedin', 'tiktok', 'youtube', 'snapchat'];
    const isSocial = socialKeywords.some(keyword => mediaTypeLower.includes(keyword));
    
    // If specific filter type is provided, check against that
    if (filterType) {
      switch (filterType) {
        case 'social':
          return isSocial;
        case 'paid':
          return !isSocial;
        case 'all':
        default:
          return true;
      }
    }
    
    // If no specific filter, check against current selected filters
    if (mediaFilters.includes('all')) {
      return true;
    }
    
    return mediaFilters.some(filter => {
      switch (filter) {
        case 'social':
          return isSocial;
        case 'paid':
          return !isSocial;
        default:
          return true;
      }
    });
  };

  /**
   * Handle media filter checkbox changes
   */
  const handleMediaFilterChange = (filterValue) => {
    setMediaFilters(prev => {
      let newFilters;
      
      if (filterValue === 'all') {
        // If 'all' is selected, clear other selections
        newFilters = prev.includes('all') ? [] : ['all'];
      } else {
        // Remove 'all' if other specific filters are selected
        const filteredPrev = prev.filter(f => f !== 'all');
        
        if (filteredPrev.includes(filterValue)) {
          // Remove the filter if already selected
          newFilters = filteredPrev.filter(f => f !== filterValue);
        } else {
          // Add the filter
          newFilters = [...filteredPrev, filterValue];
        }
        
        // If no specific filters selected, default to 'all'
        if (newFilters.length === 0) {
          newFilters = ['all'];
        }
      }
      
      return newFilters;
    });
    setBrandFilter('all'); // Reset brand filter when media filter changes
  };

  /**
   * Xử lý dữ liệu từ budget API để tạo structure monthly view
   */
  const processBudgetDataForMonthlyView = (budgetData, specificFilter = null, allMonths = null) => {
    if (!budgetData || !Array.isArray(budgetData)) {
      return generateMockData(specificFilter, allMonths);
    }

    // Nếu allMonths được truyền vào (cho multiple tables), sử dụng nó thay vì tạo mới
    // Điều này đảm bảo tất cả bảng có cùng header tháng
    let monthsToUse = allMonths;
    const monthsSet = new Set();
    const processedBrands = [];

    // Nếu không có allMonths (lần đầu gọi), collect tất cả tháng từ toàn bộ data
    if (!allMonths) {
      budgetData.forEach(brandData => {
        if (brandData.campaigns && Array.isArray(brandData.campaigns)) {
          brandData.campaigns.forEach(campaign => {
            if (campaign.media_types && Array.isArray(campaign.media_types)) {
              campaign.media_types.forEach(mediaType => {
                if (mediaType.monthly_breakdown && Array.isArray(mediaType.monthly_breakdown)) {
                  mediaType.monthly_breakdown.forEach(monthData => {
                    if (monthData.month) {
                      monthsSet.add(monthData.month);
                    }
                  });
                }
              });
            }
          });
        }
      });

      // Tạo danh sách tháng từ tất cả data (không filter)
      const sortedMonths = Array.from(monthsSet).sort().map(monthKey => {
        const date = new Date(monthKey + '-01');
        return {
          key: monthKey,
          label: date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
        };
      });

      monthsToUse = sortedMonths.length > 0 ? sortedMonths : generateMonthList();
    }

    budgetData.forEach(brandData => {
      const brand = {
        brand: brandData.brand_name || 'Unknown Brand',
        campaigns: {},
        monthlyTotals: {}
      };

      // Xử lý campaigns
      if (brandData.campaigns && Array.isArray(brandData.campaigns)) {
        brandData.campaigns.forEach(campaign => {
          const campaignData = {
            campaign: campaign.campaign_name || 'Unknown Campaign',
            monthlyData: {}
          };

          // Xử lý monthly data từ tất cả media types (với filter)
          if (campaign.media_types && Array.isArray(campaign.media_types)) {
            campaign.media_types
              .filter(mediaType => specificFilter 
                ? isMediaTypeIncluded(mediaType.media_type, specificFilter)
                : isMediaTypeIncluded(mediaType.media_type)
              )
              .forEach(mediaType => {
                if (mediaType.monthly_breakdown && Array.isArray(mediaType.monthly_breakdown)) {
                  mediaType.monthly_breakdown.forEach(monthData => {
                    const month = monthData.month;
                    if (month) {
                      // Cộng dồn cost cho campaign (từ nhiều media types)
                      const currentValue = campaignData.monthlyData[month] || { actual_cost: 0, planning_cost: 0, given_cost: 0 };
                      const actualCost = parseFloat(monthData.actual_cost) || 0;
                      const planningCost = parseFloat(monthData.planning_cost) || 0;
                      const givenCost = parseFloat(monthData.given_cost) || 0;
                      
                      // Lưu trữ tất cả metrics
                      campaignData.monthlyData[month] = {
                        actual_cost: currentValue.actual_cost + actualCost,
                        planning_cost: currentValue.planning_cost + planningCost,
                        given_cost: currentValue.given_cost + givenCost
                      };
                    }
                  });
                }
              });
          }

          // Chỉ thêm campaign nếu có dữ liệu sau khi filter
          if (Object.keys(campaignData.monthlyData).length > 0) {
            brand.campaigns[campaign.campaign_name || 'Unknown Campaign'] = campaignData;
          }
        });
      }

      // Tính total cho brand từ tất cả campaigns
      Object.values(brand.campaigns).forEach(campaign => {
        Object.entries(campaign.monthlyData).forEach(([month, metrics]) => {
          if (!brand.monthlyTotals[month]) {
            brand.monthlyTotals[month] = { actual_cost: 0, planning_cost: 0, given_cost: 0 };
          }
          brand.monthlyTotals[month].actual_cost += metrics.actual_cost;
          brand.monthlyTotals[month].planning_cost += metrics.planning_cost;
          brand.monthlyTotals[month].given_cost += metrics.given_cost;
        });
      });

      // Chỉ thêm brand nếu có campaigns sau khi filter
      if (Object.keys(brand.campaigns).length > 0) {
        processedBrands.push(brand);
      }
    });

    // Extract available brands for filter
    const brands = processedBrands.map(brand => brand.brand).sort();
    if (!specificFilter) {
      setAvailableBrands(brands);
    }

    // Filter by brand if not 'all'
    const filteredBrands = brandFilter === 'all' 
      ? processedBrands 
      : processedBrands.filter(brand => brand.brand === brandFilter);

    return {
      months: monthsToUse,
      brands: filteredBrands
    };
  };

  /**
   * Tạo mock data để test với media types
   */
  const generateMockData = (specificFilter = null, allMonths = null) => {
    const months = allMonths || generateMonthList();
    
    // Mock campaigns với media types khác nhau
    const mockCampaigns = {
      'Bia Viet': [
        { name: 'Bia Viet Billboard 2025', mediaTypes: ['OOH', 'TV'] },
        { name: 'Summer Social Campaign', mediaTypes: ['Social', 'Digital'] }
      ],
      'Heineken': [
        { name: 'Heineken 0.0 Festive 2025', mediaTypes: ['TV', 'Radio'] },
        { name: 'Social Media Campaign', mediaTypes: ['Social'] }
      ],
      'Tiger': [
        { name: 'Tiger OOH 2025', mediaTypes: ['OOH'] },
        { name: 'Tiger Social Activation', mediaTypes: ['Social', 'Digital'] }
      ]
    };

    const brands = Object.entries(mockCampaigns).map(([brandName, campaigns]) => ({
      brand: brandName,
      campaigns: campaigns.reduce((acc, campaign) => {
        // Chỉ bao gồm campaigns có media types phù hợp với filter
        const hasMatchingMediaType = campaign.mediaTypes.some(mediaType => 
          specificFilter 
            ? isMediaTypeIncluded(mediaType, specificFilter)
            : isMediaTypeIncluded(mediaType)
        );
        
        if (hasMatchingMediaType) {
          acc[campaign.name] = {
            campaign: campaign.name,
            monthlyData: {}
          };
        }
        return acc;
      }, {}),
      monthlyTotals: {}
    })).filter(brand => Object.keys(brand.campaigns).length > 0); // Chỉ giữ brands có campaigns

    // Tạo dữ liệu giả cho từng tháng
    brands.forEach(brand => {
      months.forEach(month => {
        let brandTotal = { actual_cost: 0, planning_cost: 0, given_cost: 0 };
        Object.values(brand.campaigns).forEach(campaign => {
          const actualCost = Math.floor(Math.random() * 500000) + 50000;
          const planningCost = actualCost + Math.floor(Math.random() * 100000);
          const givenCost = planningCost + Math.floor(Math.random() * 50000);
          
          campaign.monthlyData[month.key] = {
            actual_cost: actualCost,
            planning_cost: planningCost,
            given_cost: givenCost
          };
          
          brandTotal.actual_cost += actualCost;
          brandTotal.planning_cost += planningCost;
          brandTotal.given_cost += givenCost;
        });
        brand.monthlyTotals[month.key] = brandTotal;
      });
    });

    // Extract available brands for filter
    const brandNames = brands.map(brand => brand.brand).sort();
    if (!specificFilter) {
      setAvailableBrands(brandNames);
    }

    // Filter by brand if not 'all'
    const filteredBrands = brandFilter === 'all' 
      ? brands 
      : brands.filter(brand => brand.brand === brandFilter);

    return { months, brands: filteredBrands };
  };

  /**
   * Tạo danh sách 12 tháng gần nhất
   */
  const generateMonthList = () => {
    const months = [];
    const currentDate = new Date();
    
    for (let i = 11; i >= 0; i--) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const monthKey = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;
      const monthLabel = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      months.push({ key: monthKey, label: monthLabel });
    }
    
    return months;
  };

  /**
   * Format currency - full number display with smaller font
   */
  const formatCurrency = (value) => {
    if (!value) return '0';
    
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  /**
   * Toggle brand expansion
   */
  const toggleBrandExpansion = (brandName) => {
    setExpandedBrands(prev => {
      const newSet = new Set(prev);
      if (newSet.has(brandName)) {
        newSet.delete(brandName);
      } else {
        newSet.add(brandName);
      }
      return newSet;
    });
  };



  /**
   * Render brand row
   */
  const renderBrandRow = (brandData, index, currentMediaFilter = null) => {
    const brandKey = currentMediaFilter ? `${brandData.brand}-${currentMediaFilter}` : brandData.brand;
    const isExpanded = expandedBrands.has(brandKey);
    
    return (
      <React.Fragment key={brandKey}>
        {/* Brand Header Row */}
        <TableRow 
          hover
          sx={{ 
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
            borderLeft: `4px solid ${theme.palette.primary.main}`,
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.12),
              cursor: 'pointer'
            }
          }}
          onClick={() => toggleBrandExpansion(brandKey)}
        >
          <TableCell sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            minWidth: 160,
            padding: '6px 8px'
          }}>
            <IconButton
              size="small"
              sx={{ mr: 1, padding: '2px' }}
            >
              {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ 
                fontSize: '0.7rem',
                lineHeight: 1.2,
                color: 'black'
              }}>
                {brandData.brand}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                <Chip
                  label={`${Object.keys(brandData.campaigns).length} campaigns`}
                  size="small"
                  color={currentMediaFilter === 'all' || !currentMediaFilter ? 'default' : currentMediaFilter === 'social' ? 'success' : 'warning'}
                  sx={{ 
                    fontSize: '0.5rem',
                    height: 18
                  }}
                />
                {currentMediaFilter && currentMediaFilter !== 'all' && (
                  <Tooltip title={`${currentMediaFilter === 'social' ? 'Social Media' : 'Paid Media'} campaigns only`}>
                    <Chip
                      label={currentMediaFilter === 'social' ? 'Social' : 'Paid'}
                      size="small"
                      variant="outlined"
                      color={currentMediaFilter === 'social' ? 'success' : 'warning'}
                      sx={{ 
                        fontSize: '0.45rem',
                        height: 16
                      }}
                    />
                  </Tooltip>
                )}
              </Box>
            </Box>
          </TableCell>
          
          {/* Monthly totals for brand */}
          {data.months?.map(month => (
            <TableCell key={month.key} align="right" sx={{ 
              padding: '6px 8px',
              minWidth: 100,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              <Typography variant="body2" fontWeight="bold" sx={{ 
                fontSize: '0.7rem',
                lineHeight: 1.2,
                color: 'black'
              }}>
                {formatCurrency(brandData.monthlyTotals[month.key]?.[selectedMetric] || 0)}
              </Typography>
            </TableCell>
          ))}
        </TableRow>

        {/* Expandable Campaign Rows */}
        {isExpanded && Object.values(brandData.campaigns).map((campaign, campaignIndex) => (
          <TableRow 
            key={`${campaign.campaign}-${currentMediaFilter || 'default'}`} 
            component={motion.tr}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ 
              duration: 0.3,
              delay: campaignIndex * 0.05,
              ease: 'easeOut'
            }}
            sx={{ 
              backgroundColor: alpha(theme.palette.grey[50], 0.6),
              borderLeft: `3px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
              '&:hover': {
                backgroundColor: alpha(theme.palette.grey[100], 0.8)
              }
            }}
          >
            <TableCell sx={{ 
              pl: 4, 
              border: 'none',
              padding: '6px 8px',
              minWidth: 160
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CampaignIcon sx={{ 
                  mr: 1, 
                  fontSize: 14, 
                  color: theme.palette.text.secondary 
                }} />
                <Typography variant="body2" sx={{ 
                  fontSize: '0.65rem',
                  lineHeight: 1.2,
                  color: 'black'
                }}>
                  {campaign.campaign}
                </Typography>
              </Box>
            </TableCell>
            
            {data.months?.map(month => (
              <TableCell key={month.key} align="right" sx={{ 
                border: 'none',
                padding: '6px 8px',
                minWidth: 100,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                <Typography variant="body2" sx={{ 
                  fontSize: '0.65rem',
                  lineHeight: 1.2,
                  fontWeight: 'bold',
                  color: 'black'
                }}>
                  {formatCurrency(campaign.monthlyData[month.key]?.[selectedMetric] || 0)}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        ))}
      </React.Fragment>
    );
  };

  /**
   * Render individual table for specific media filter
   */
  const renderTable = (tableData, mediaFilter = null, tableIndex = 0) => {
    /**
     * Calculate totals across all brands for each month for this specific table
     */
    const calculateTableGrandTotals = () => {
      const grandTotals = {};
      
      tableData.brands?.forEach(brandData => {
        Object.entries(brandData.monthlyTotals).forEach(([month, metrics]) => {
          if (!grandTotals[month]) {
            grandTotals[month] = { actual_cost: 0, planning_cost: 0, given_cost: 0 };
          }
          grandTotals[month].actual_cost += metrics.actual_cost || 0;
          grandTotals[month].planning_cost += metrics.planning_cost || 0;
          grandTotals[month].given_cost += metrics.given_cost || 0;
        });
      });
      
      return grandTotals;
    };

    /**
     * Render total row at bottom of specific table
     */
    const renderTableTotalRow = () => {
      const grandTotals = calculateTableGrandTotals();
      
      return (
        <TableRow sx={{ 
          backgroundColor: 'rgba(25, 118, 210, 0.08)',
          '& .MuiTableCell-root': {
            fontWeight: 'bold',
            borderTop: '2px solid #1976d2',
            padding: '8px 12px'
          }
        }}>
          <TableCell sx={{ 
            fontWeight: 'bold', 
            fontSize: '0.7rem',
            minWidth: 160,
            color: 'black'
          }}>
            TOTAL ({tableData.brands?.length || 0} brands)
            {mediaFilter && (
              <Typography variant="caption" sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.8 }}>
                {mediaFilter === 'social' ? 'Social Media' : mediaFilter === 'paid' ? 'Paid Media' : 'All Media'}
              </Typography>
            )}
          </TableCell>
          
          {/* Monthly grand totals */}
          {tableData.months?.map(month => (
            <TableCell key={month.key} align="right" sx={{ 
              fontWeight: 'bold', 
              fontSize: '0.65rem',
              minWidth: 100,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              color: 'black'
            }}>
              {formatCurrency(grandTotals[month.key]?.[selectedMetric] || 0)}
            </TableCell>
          ))}
        </TableRow>
      );
    };

    const filterOption = mediaFilterOptions.find(opt => opt.value === mediaFilter);
    const tableTitle = mediaFilter ? 
      `${filterOption?.label || mediaFilter} Performance` : 
      'Monthly Performance by Brand';

    return (
      <motion.div 
        key={`table-${mediaFilter || 'main'}`}
        variants={itemVariants}
        initial="hidden"
        animate="visible"
        style={{ marginBottom: mediaFilter ? '3rem' : '0' }}
      >
        {mediaFilter && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ 
              fontWeight: 700, 
              fontSize: { xs: '0.75rem', sm: '0.85rem' }, 
              color: 'black',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              lineHeight: 1.2
            }}>
              {tableTitle}
              <Chip
                label={filterOption?.label || mediaFilter}
                size="small"
                color={filterOption?.color || 'default'}
                variant="filled"
              />
            </Typography>
            <Typography variant="body2" sx={{ 
              fontSize: { xs: '0.55rem', sm: '0.6rem' }, 
              color: 'black', 
              opacity: 0.8,
              lineHeight: 1.3
            }}>
              {tableData.brands?.length || 0} brands • {tableData.brands?.reduce((total, brand) => total + Object.keys(brand.campaigns).length, 0) || 0} campaigns
            </Typography>
          </Box>
        )}

        {/* Table Container */}
        <TableContainer component={Paper} sx={{ 
          maxHeight: '75vh', 
          height: 'auto',
          overflow: 'auto',
          maxWidth: '100%',
          '& .MuiTable-root': {
            minWidth: 800,
            fontSize: { xs: '0.7rem', sm: '0.8rem' }
          },
          // Responsive behavior
          '@media (max-width: 1200px)': {
            '& .MuiTable-root': {
              minWidth: 700,
            }
          },
          '@media (max-width: 900px)': {
            '& .MuiTable-root': {
              minWidth: 600,
            }
          }
        }}>
          <Table aria-label="monthly performance table" stickyHeader>
            <TableHead>
              <TableRow sx={{
                '& .MuiTableCell-head': {
                  backgroundColor: theme => `${theme.palette.primary.main} !important`,
                  color: '#fff !important',
                  fontWeight: 700,
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  opacity: 1,
                  whiteSpace: 'nowrap',
                  minWidth: 100,
                  padding: '8px 12px',
                  '&:hover': {
                    backgroundColor: theme => `${theme.palette.primary.dark} !important`
                  }
                },
                '& .MuiTableCell-stickyHeader': {
                  backgroundColor: theme => `${theme.palette.primary.main} !important`,
                  backgroundImage: 'none !important'
                }
              }}>
                <TableCell sx={{ 
                  minWidth: 160, 
                  fontSize: { xs: '0.65rem', sm: '0.7rem' },
                  fontWeight: 700
                }}>
                  Brand / Campaign
                </TableCell>
                {tableData.months?.map(month => (
                  <TableCell key={month.key} align="right" sx={{ 
                    minWidth: 100, 
                    fontSize: { xs: '0.65rem', sm: '0.7rem' }
                  }}>
                    <Box>
                      <Typography variant="caption" sx={{ 
                        fontWeight: 700, 
                        fontSize: { xs: '0.55rem', sm: '0.6rem' },
                        lineHeight: 1.2,
                        color: 'inherit'
                      }}>
                        {month.label}
                      </Typography>
                      <Typography variant="caption" sx={{ 
                        display: 'block', 
                        fontSize: { xs: '0.45rem', sm: '0.5rem' },
                        lineHeight: 1,
                        color: 'inherit',
                        opacity: 0.8
                      }}>
                        {selectedMetric === 'actual_cost' && 'Actual'}
                        {selectedMetric === 'planning_cost' && 'Planning'}
                        {selectedMetric === 'given_cost' && 'Given'}
                      </Typography>
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {tableData.brands?.map((brandData, index) => renderBrandRow(brandData, index, mediaFilter))}
              {tableData.brands && tableData.brands.length > 0 && renderTableTotalRow()}
            </TableBody>
          </Table>
        </TableContainer>

        {(!tableData.brands || tableData.brands.length === 0) && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" gutterBottom sx={{ fontSize: '0.7rem', color: 'black' }}>
              No data available {mediaFilter ? `for ${filterOption?.label || mediaFilter}` : ''}
            </Typography>
          </Box>
        )}
      </motion.div>
    );
  };

  if (loading || parentLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={30} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2, fontSize: '0.7rem' }}>
        {error}
      </Alert>
    );
  }

  // Animation variants for staggered animations
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 }
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <motion.div 
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div variants={itemVariants}>
          {/* Header Section */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="h6" sx={{ 
                  fontWeight: 700, 
                  fontSize: { xs: '0.8rem', sm: '0.9rem' }, 
                  color: 'black',
                  lineHeight: 1.2
                }}>
                  Monthly Performance by Brand
                </Typography>
                {mediaFilters.map(filter => {
                  const filterOption = mediaFilterOptions.find(opt => opt.value === filter);
                  return (
                    <Chip
                      key={filter}
                      label={filterOption?.label || filter}
                      size="small"
                      color={filterOption?.color || 'default'}
                      variant="outlined"
                    />
                  );
                })}
                {brandFilter !== 'all' && (
                  <Chip
                    label={brandFilter}
                    size="small"
                    color="secondary"
                    variant="filled"
                    sx={{ ml: 1 }}
                  />
                )}
              </Box>
              <Typography variant="body2" sx={{ 
                fontSize: { xs: '0.55rem', sm: '0.6rem' }, 
                color: 'black',
                lineHeight: 1.3
              }}>
                Brand performance data grouped by month with expandable campaign details
                {mediaFilters.length > 1 && ` • Showing ${mediaFilters.length} media types separately`}
                {brandFilter !== 'all' && ` • Filtered by: ${brandFilter}`}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              {/* Media Filter Checkboxes */}
              <FormControl component="fieldset" size="small">
                <FormLabel component="legend" sx={{ 
                  fontSize: { xs: '0.55rem', sm: '0.6rem' }, 
                  color: 'black', 
                  mb: 1,
                  fontWeight: 600
                }}>
                  Media Types
                </FormLabel>
                <FormGroup row sx={{ gap: 1 }}>
                  {mediaFilterOptions.map((option) => (
                    <FormControlLabel
                      key={option.value}
                      control={
                        <Checkbox
                          checked={mediaFilters.includes(option.value)}
                          onChange={() => handleMediaFilterChange(option.value)}
                          size="small"
                          sx={{ 
                            padding: '4px',
                            '& .MuiSvgIcon-root': { fontSize: 18 }
                          }}
                        />
                      }
                      label={
                        <Typography sx={{ 
                          fontSize: { xs: '0.55rem', sm: '0.6rem' }, 
                          color: 'black',
                          fontWeight: 500
                        }}>
                          {option.label}
                        </Typography>
                      }
                      sx={{ margin: 0 }}
                    />
                  ))}
                </FormGroup>
              </FormControl>
                
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel sx={{ 
                  fontSize: { xs: '0.55rem', sm: '0.6rem' }, 
                  color: 'black',
                  fontWeight: 600
                }}>Metric</InputLabel>
                <Select
                  value={selectedMetric}
                  onChange={(e) => setSelectedMetric(e.target.value)}
                  label="Metric"
                  sx={{ 
                    fontSize: { xs: '0.55rem', sm: '0.6rem' }, 
                    color: 'black'
                  }}
                >
                  <MenuItem value="actual_cost" sx={{ 
                    fontSize: { xs: '0.55rem', sm: '0.6rem' }, 
                    color: 'black' 
                  }}>Actual Cost</MenuItem>
                  <MenuItem value="planning_cost" sx={{ 
                    fontSize: { xs: '0.55rem', sm: '0.6rem' }, 
                    color: 'black' 
                  }}>Planning Cost</MenuItem>
                  <MenuItem value="given_cost" sx={{ 
                    fontSize: { xs: '0.55rem', sm: '0.6rem' }, 
                    color: 'black' 
                  }}>Given Cost</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          {/* Brand Filter Tabs */}
          <Tabs
            value={brandFilter}
            onChange={(e, newValue) => setBrandFilter(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              mb: 2,
              '& .MuiTabs-scrollButtons': {
                width: 30,
                '&.Mui-disabled': {
                  opacity: 0.3
                }
              },
              '& .MuiTab-root': {
                fontSize: { xs: '0.55rem', sm: '0.6rem' },
                textTransform: 'none',
                minWidth: 60,
                minHeight: 36,
                padding: { xs: '4px 8px', sm: '6px 12px' },
                color: 'black',
                fontWeight: 600,
                '&.Mui-selected': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  color: 'black'
                }
              }
            }}
          >
            <Tab value="all" label="All" />
            {availableBrands.map(brand => (
              <Tab key={brand} value={brand} label={brand} />
            ))}
          </Tabs>

          {/* Render Tables */}
          {(() => {
            // If 'all' is selected or only one filter is selected, show single table
            if (mediaFilters.includes('all') || mediaFilters.length === 1) {
              return renderTable(data);
            }
            
            // If multiple specific filters are selected, show separate tables
            // First, get the common months list from the main data to ensure all tables have same headers
            const commonMonths = data.months || generateMonthList();
            
            return mediaFilters.map((filter, index) => {
              try {
                // Use the original data but process it with the specific filter and common months
                const tableData = processBudgetDataForMonthlyView(data.originalData || [], filter, commonMonths);
                return renderTable(tableData, filter, index);
              } catch (err) {
                console.error(`Error processing data for filter ${filter}:`, err);
                const mockTableData = generateMockData(filter, commonMonths);
                return renderTable(mockTableData, filter, index);
              }
            });
          })()}

          {/* No data message */}
          {(!data.brands || data.brands.length === 0) && (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body2" gutterBottom sx={{ fontSize: '0.7rem', color: 'black' }}>
                No data available for monthly view
              </Typography>
              {(mediaFilters.length > 1 || brandFilter !== 'all') && (
                <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'black' }}>
                  Try changing the {mediaFilters.length > 1 ? 'media' : ''} {brandFilter !== 'all' ? 'brand' : ''} filter to see more data
                </Typography>
              )}
            </Box>
          )}
        </motion.div>
      </motion.div>
    </Box>
  );
};

export default MonthlyByMonthTable; 